import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  InputNumber,
  Row,
  Tag,
  TimePicker,
  Typography,
  Table,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { EditableCorral, CorralParticipant } from "../types/corral.types";

const { Text } = Typography;

interface CorralAssignmentStepProps {
  editableCorrals: EditableCorral[];
  onChange: (corrals: EditableCorral[]) => void;
  onBack: () => void;
  onPreview: () => void;
}

// Lighten a hex color for the card background
const alpha = (hex: string, opacity: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${opacity})`;
};

const participantCols: ColumnsType<CorralParticipant> = [
  {
    title: "BIB",
    dataIndex: "bib_number",
    key: "bib",
    width: 70,
    render: (b) =>
      b ? (
        <Text strong style={{ color: "#1890ff" }}>
          {b}
        </Text>
      ) : (
        <Tag color="warning" style={{ fontSize: 10 }}>
          No BIB
        </Tag>
      ),
  },
  {
    title: "Name",
    key: "name",
    render: (_, r) => (
      <Text>
        {r.first_name} {r.last_name}
      </Text>
    ),
  },
  {
    title: "Time",
    dataIndex: "expected_finish_time",
    key: "time",
    width: 80,
    render: (t) =>
      t !== undefined ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t} min
        </Text>
      ) : (
        <Text type="secondary">—</Text>
      ),
  },
];

const CorralAssignmentStep: React.FC<CorralAssignmentStepProps> = ({
  editableCorrals,
  onChange,
  onBack,
  onPreview,
}) => {
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  const update = (key: string, patch: Partial<EditableCorral>) => {
    onChange(editableCorrals.map((c) => (c.key === key ? { ...c, ...patch } : c)));
  };

  const totalAssigned = editableCorrals.reduce(
    (s, c) => s + c.participants.length,
    0,
  );
  const totalCapacity = editableCorrals.reduce(
    (s, c) => s + c.max_participants,
    0,
  );
  const isOverCapacity = editableCorrals.some(
    (c) => c.participants.length > c.max_participants,
  );

  return (
    <div>
      {/* ── Validation Banner ────────────────────────────────────────────── */}
      {isOverCapacity && (
        <Alert
          type="error"
          showIcon
          message="Some corrals exceed their max capacity. Increase the limit or redistribute participants."
          style={{ marginBottom: 16 }}
        />
      )}

      {/* ── Summary row ──────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          gap: 24,
          padding: "12px 20px",
          background: "#f8fafc",
          borderRadius: 10,
          marginBottom: 20,
          border: "1px solid #e2e8f0",
        }}
      >
        <div>
          <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase" }}>
            Corrals
          </Text>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{editableCorrals.length}</div>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase" }}>
            Assigned
          </Text>
          <div style={{ fontWeight: 700, fontSize: 18, color: "#16a34a" }}>
            {totalAssigned}
          </div>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase" }}>
            Total Capacity
          </Text>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{totalCapacity}</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Edit names, start times, and capacities below.
          </Text>
        </div>
      </div>

      {/* ── Corral Cards Grid ─────────────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {editableCorrals.map((corral) => {
          const over = corral.participants.length > corral.max_participants;
          const isExpanded = expandedKeys.includes(corral.key);

          return (
            <Col key={corral.key} xs={24} sm={12} lg={8}>
              <Card
                bordered={false}
                style={{
                  borderLeft: `4px solid ${corral.color}`,
                  backgroundColor: alpha(corral.color, 0.05),
                  boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                }}
              >
                {/* Top: name badge + count */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 14,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        background: corral.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 900,
                        fontSize: 17,
                        color: "#fff",
                        flexShrink: 0,
                      }}
                    >
                      {corral.name.slice(0, 2)}
                    </div>
                    {/* Editable name */}
                    <input
                      value={corral.name}
                      maxLength={8}
                      onChange={(e) => update(corral.key, { name: e.target.value })}
                      style={{
                        border: "none",
                        background: "transparent",
                        fontWeight: 700,
                        fontSize: 15,
                        color: "#1e293b",
                        width: 90,
                        outline: "none",
                        borderBottom: "1.5px dashed #cbd5e1",
                        padding: "2px 0",
                      }}
                    />
                  </div>
                  <Tag
                    color={over ? "error" : "default"}
                    icon={<TeamOutlined />}
                    style={{ fontWeight: 600 }}
                  >
                    {corral.participants.length}
                  </Tag>
                </div>

                {/* BIB range (read-only) */}
                {!corral.is_default && (
                  <Tag
                    style={{
                      marginBottom: 12,
                      borderRadius: 6,
                      fontSize: 11,
                      background: alpha(corral.color, 0.1),
                      borderColor: alpha(corral.color, 0.3),
                      color: "#374151",
                    }}
                  >
                    BIB {corral.bib_start}–{corral.bib_end}
                  </Tag>
                )}
                {corral.is_default && (
                  <Tag
                    style={{ marginBottom: 12, borderRadius: 6, fontSize: 11 }}
                  >
                    No BIB (overflow)
                  </Tag>
                )}

                {/* Start Time */}
                <div style={{ marginBottom: 10 }}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: "block", marginBottom: 4 }}
                  >
                    Start Time
                  </Text>
                  <TimePicker
                    format="HH:mm"
                    value={corral.start_time ? dayjs(corral.start_time, "HH:mm") : null}
                    onChange={(_, v) =>
                      update(corral.key, { start_time: v as string || undefined })
                    }
                    placeholder="e.g. 09:00"
                    style={{ width: "100%" }}
                    size="small"
                    allowClear
                  />
                </div>

                {/* Max Participants */}
                <div style={{ marginBottom: 12 }}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: "block", marginBottom: 4 }}
                  >
                    Max Participants
                  </Text>
                  <InputNumber
                    min={1}
                    value={corral.max_participants}
                    onChange={(v) => v && update(corral.key, { max_participants: v })}
                    style={{ width: "100%" }}
                    size="small"
                    status={over ? "error" : undefined}
                  />
                  {over && (
                    <Text style={{ fontSize: 10, color: "#ef4444" }}>
                      Exceeds capacity by{" "}
                      {corral.participants.length - corral.max_participants}
                    </Text>
                  )}
                </div>

                {/* Expand participants */}
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, color: corral.color }}
                  onClick={() =>
                    setExpandedKeys((prev) =>
                      isExpanded
                        ? prev.filter((k) => k !== corral.key)
                        : [...prev, corral.key],
                    )
                  }
                >
                  {isExpanded ? "Hide" : "View"} participants
                </Button>

                {isExpanded && (
                  <Table
                    columns={participantCols}
                    dataSource={corral.participants}
                    rowKey="id"
                    size="small"
                    pagination={
                      corral.participants.length > 5
                        ? { pageSize: 5, size: "small", simple: true }
                        : false
                    }
                    style={{ marginTop: 10 }}
                    scroll={{ x: 320 }}
                  />
                )}
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* ── Navigation ───────────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          Back
        </Button>
        <Button
          type="primary"
          icon={<ArrowRightOutlined />}
          onClick={onPreview}
          disabled={isOverCapacity || editableCorrals.length === 0}
        >
          Preview
        </Button>
      </div>
    </div>
  );
};

export default CorralAssignmentStep;
